import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  BagFillIcon,
  HouseFillIcon,
  PersonCropCircleFillIcon,
  type CupertinoIconProps,
} from './icons/cupertino';
import { Button, ErrorState, Group, Icon, Skeleton } from '@store/ui';
import { api, ApiError, type SessionResponse } from './api';
import { getTelegramPhotoUrl, initTelegram, isInsideTelegram, useBackButton } from './telegram';
import { initialPullState, pullStatusText, reducePull, type PullState } from './pull-to-refresh';
import {
  AdminSection,
  CatalogScreen,
  HomeScreen,
  OrderScreen,
  OrdersScreen,
  ProductScreen,
  ProfileScreen,
  resolveAdminRoute,
  SupportScreen,
  type AdminRoute,
} from './screens';

const STORE_NAME = (import.meta.env.VITE_STORE_NAME as string | undefined) ?? 'Satin market';

/**
 * The way back to the bot — only when this build was actually told which bot.
 *
 * The default used to be the literal `your_bot`, which is a real address on
 * t.me belonging to somebody else: an unconfigured build quietly sent the one
 * customer who could not sign in to a stranger's chat, and nothing on screen
 * hinted that the link was a placeholder. Now an unset, malformed or still-
 * placeholder value yields no link at all and the button is simply not offered.
 * A missing button is a visible symptom; a wrong link is not.
 */
const BOT_LINK = ((): string | null => {
  const name = ((import.meta.env.VITE_BOT_USERNAME as string | undefined) ?? '')
    .trim()
    .replace(/^@/, '');
  // Telegram usernames are 5–32 of [A-Za-z0-9_]; `your_bot` fits that shape but
  // is the value the README and the deploy templates carry as an example.
  if (name === 'your_bot') return null;
  return /^[A-Za-z0-9_]{5,32}$/.test(name) ? `https://t.me/${name}` : null;
})();

/** Hash routing keeps the app a single static bundle with no server rewrites. */
function useHashRoute(): [string, (route: string) => void] {
  const [route, setRoute] = useState(() => window.location.hash.slice(1) || '/');

  useEffect(() => {
    const onChange = () => setRoute(window.location.hash.slice(1) || '/');
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  const navigate = (next: string) => {
    window.location.hash = next;
  };

  return [route, navigate];
}

/** The three places the bar can take you. Every route resolves to one of them. */
type TabRoute = '/' | '/orders' | '/profile';

/**
 * Cupertino glyphs, vendored from Framework7 Icons (MIT) — see
 * `./icons/cupertino`. Remix stays elsewhere in the app; the bar is the one
 * place whose marks sit beside Telegram's own chrome, so it follows the iOS
 * drawing the client does.
 *
 * Three tabs. The catalogue and support are not bottom tabs: the catalogue is
 * reached from the home screen, while support remains deep-linkable for any
 * existing entry point without taking space in primary navigation.
 */
type TabGlyph =
  | { readonly kind: 'icon'; readonly Icon: (props: CupertinoIconProps) => ReactNode }
  | { readonly kind: 'avatar' };

const TABS: ReadonlyArray<{ route: TabRoute; label: string; glyph: TabGlyph }> = [
  { route: '/', label: 'Главная', glyph: { kind: 'icon', Icon: HouseFillIcon } },
  { route: '/orders', label: 'Заказы', glyph: { kind: 'icon', Icon: BagFillIcon } },
  { route: '/profile', label: 'Профиль', glyph: { kind: 'avatar' } },
];

/** The glyph box. The photograph fills the same one, so the three marks share a
    baseline. */
const GLYPH_SIZE = 26;
const AVATAR_SIZE = 26;

/**
 * The customer's own photograph in the profile tab, the way Telegram's own bar
 * does it — and `PersonCropCircleFillIcon` only when there is not one.
 *
 * There are three ways to end up with no picture and they are not the same
 * event: the client is a plain browser, so there is no Telegram user at all;
 * Telegram has a user but withholds `photo_url`, which is what privacy settings
 * do; or the URL exists and the fetch fails. The first two are known before a
 * render, the third only afterwards, so it needs `onError` — without it the tab
 * keeps a broken-image box where the face should be, which looks like a bug in
 * the shop rather than a missing avatar.
 *
 * No `active` prop: the stylesheet reads the button's own `aria-current`.
 */
function ProfileGlyph() {
  const [failed, setFailed] = useState(false);
  const photoUrl = useMemo(() => getTelegramPhotoUrl(), []);

  if (!photoUrl || failed) {
    return <PersonCropCircleFillIcon size={GLYPH_SIZE} className="tabbar__glyph" />;
  }

  return (
    <img
      className="tabbar__avatar"
      src={photoUrl}
      alt=""
      width={AVATAR_SIZE}
      height={AVATAR_SIZE}
      loading="eager"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * One reading of the hash: which screen to render, and which tab owns it.
 *
 * These used to be two independent readings of the same string — a chain of
 * `startsWith` picked the screen, and the bar ran its own `startsWith` against
 * every tab route — and they disagreed. A product shares no prefix with the
 * catalog, so `/product/x` matched nothing and put out every light in the bar
 * halfway through the one flow that matters; and had two tab routes ever shared
 * a prefix, two lights would have come on instead. Resolving once and handing
 * the same answer to both sides makes "exactly one tab is lit, and it is the
 * one you came in through" a property of the router rather than a guess the bar
 * makes from a string.
 */
/*
 * The catalogue and a product page still exist and are still routed — they are
 * simply not tabs any more. Both light Главная, because that is the screen the
 * customer came in through: tapping a service tile is what opens them, so Home
 * is where "back" leads and where the bar should say you are.
 *
 * Поддержка is the same shape since it stopped being a tab: the only door into
 * it is the «Нужна помощь?» row on Профиль, so that is the tab it lights.
 */
type Resolved =
  | { kind: 'home'; tab: '/' }
  | { kind: 'catalog'; tab: '/'; categoryId: string | null }
  | { kind: 'product'; tab: '/'; shortId: string }
  | { kind: 'orders'; tab: '/orders' }
  | { kind: 'order'; tab: '/orders'; orderId: string }
  | { kind: 'support'; tab: '/profile' }
  | { kind: 'profile'; tab: '/profile' }
  /*
   * Управление магазином, which is not a tab and never will be.
   *
   * It resolves to `/profile` because that is the only door into it — the
   * management rows live on the Профиль screen — so the bar keeps saying where
   * the owner came in, exactly as it does for a product opened from Home.
   */
  | { kind: 'admin'; tab: '/profile'; admin: AdminRoute };

function resolveRoute(route: string): Resolved {
  // The query belongs to the catalog filter alone, so the path is matched on its
  // own — otherwise an id would carry `?…` along with it.
  const [rawPath, query = ''] = route.split('?');
  const path = rawPath || '/';

  if (path.startsWith('/admin')) {
    const admin = resolveAdminRoute(path);
    if (admin) return { kind: 'admin', tab: '/profile', admin };
  }
  if (path.startsWith('/product/')) {
    return { kind: 'product', tab: '/', shortId: path.slice('/product/'.length) };
  }
  if (path.startsWith('/orders/')) {
    return { kind: 'order', tab: '/orders', orderId: path.slice('/orders/'.length) };
  }
  if (path === '/orders') return { kind: 'orders', tab: '/orders' };
  if (path === '/catalog') {
    return {
      kind: 'catalog',
      tab: '/',
      categoryId: new URLSearchParams(query).get('category'),
    };
  }
  if (path === '/support') return { kind: 'support', tab: '/profile' };
  if (path === '/profile') return { kind: 'profile', tab: '/profile' };

  // Anything unrecognised has always fallen through to Home, and the bar has to
  // say so: a bar with nothing lit reads as a broken app, not as an odd URL.
  return { kind: 'home', tab: '/' };
}

/**
 * The tab bar.
 *
 * Plain buttons inside a `<nav>`, deliberately not `role="tab"` in a
 * `role="tablist"`: these do not switch panels within one view, they change the
 * route. The URL changes, the screen is replaced wholesale, and there is no
 * `tabpanel` for a tab to own or to point at with `aria-controls`. Announcing
 * tab semantics without them promises a screen-reader user arrow-key movement
 * between panels that do not exist — worse than the plain landmark that
 * describes what this actually is. `aria-current="page"` is the complete and
 * correct statement of selection here.
 *
 * It is also the *only* statement of it: no `--active` modifier class, so no
 * cell's geometry depends on which tab you are on. Each button is a bare flex
 * child of `.tabbar` — three identical siblings, no wrappers, no stray text
 * nodes — so the three cells are one division of the bar and the labels sit on
 * one line. The glyph carries `aria-hidden` because its label
 * is visible beside it; an icon with its own name would announce every tab
 * twice, and the accessible name is exactly the Russian label.
 */
function TabBar({
  current,
  onNavigate,
}: {
  readonly current: TabRoute;
  readonly onNavigate: (route: string) => void;
}) {
  return (
    <div className="bottom-nav">
      <nav className="tabbar" aria-label="Основная навигация">
        {TABS.map((tab) => {
          const active = tab.route === current;
          return (
            <button
              key={tab.route}
              type="button"
              className="tabbar__item"
              aria-current={active ? 'page' : undefined}
              onClick={() => onNavigate(tab.route)}
            >
              {tab.glyph.kind === 'icon' ? (
                <tab.glyph.Icon size={GLYPH_SIZE} className="tabbar__glyph" />
              ) : (
                <ProfileGlyph />
              )}
              {/* `data-label` is the same word again, and the stylesheet renders
                it invisibly at semibold to reserve the selected width — so
                selecting a tab cannot shift its own caption sideways. */}
              <span className="tabbar__label" data-label={tab.label}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * A wall between one broken screen and the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it,
 * so a single bad field in one response — a malformed amount, a date that will
 * not parse — took the customer from a working shop to a blank screen with no
 * way back. This catches it at the screen, leaving the shell and the tab bar
 * standing, so the other three tabs still work while one is broken.
 *
 * Keyed by route at the call site: React keeps a boundary in its error state
 * forever otherwise, and navigating away from the screen that threw would show
 * this message on every screen after it.
 */
class ScreenBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="container">
        <ErrorState
          title="Экран не открылся"
          text="Что-то в ответе магазина мы не смогли отобразить. Другие разделы работают."
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }
}

/**
 * The first paint, shaped like the screen standing behind it.
 *
 * Three stacked bars of 34/44/168px used to sit here. They resembled nothing
 * this app ever renders, so nothing lined up when the data landed and the whole
 * screen re-laid itself in one visible jolt. This is the home screen's own
 * geometry — title, subtitle, category strip, section label, three product rows
 * — and the rows are drawn exactly like `ListSkeleton` in `screens.tsx`, which
 * is what takes over the instant authentication finishes: the placeholder is
 * replaced by an identical placeholder rather than by a different layout.
 *
 * The category strip belongs to Home alone, so a deep link into another tab
 * does not get promised one.
 */
function LaunchSkeleton({ home }: { readonly home: boolean }) {
  return (
    <div className="container stack">
      {/* Every `Skeleton` is `aria-hidden`, so without this the launch screen
          announces nothing at all. */}
      <span className="sr-only">Загружаем магазин</span>

      <header>
        <Skeleton height={34} width="64%" />
        <div style={{ height: 'var(--space-1)' }} />
        <Skeleton height={20} width="86%" />
      </header>

      {home ? <Skeleton height={42} /> : null}

      <section>
        <Skeleton height={13} width="30%" />
        <div style={{ height: 'var(--space-2)' }} />
        <Group>
          {[0, 1, 2].map((row) => (
            <div key={row} className="product">
              <span className="product__body">
                <Skeleton height={17} width="62%" />
                <div style={{ height: 6 }} />
                <Skeleton height={14} width="44%" />
                <div style={{ height: 10 }} />
                <Skeleton height={17} width="28%" />
              </span>
            </div>
          ))}
        </Group>
      </section>
    </div>
  );
}

/**
 * The pull gesture, wired to a scroll container.
 *
 * Everything that decides anything is in `reducePull`; this only turns touches
 * into events and runs the effect. `passive: true` on all three listeners is
 * deliberate — the gesture never calls `preventDefault`, so it must not claim it
 * might: a non-passive listener on a scroller costs a frame on every touch, and
 * what keeps the page from peeling off the window is the CSS containment, not
 * this.
 */
/** Off-screen but announced: the standard clip-rect, not `display: none`. */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * The spinner that follows the finger, and takes no space when it is not.
 *
 * `position: absolute` so it cannot contribute height: the whole of this app's
 * layout work is about nothing appearing above the content and pushing it, and
 * an indicator in the flow would do exactly that on every pull.
 *
 * No transition on the drag — the position IS the finger, and easing a value
 * that is already continuous only adds lag. The spring back after release is the
 * one place motion helps, and `prefers-reduced-motion` drops it: the indicator
 * disappears rather than travelling, which is the non-vestibular equivalent, not
 * an absence of feedback. The status line above says the same thing in words
 * either way.
 */
function PullIndicator({ state }: { readonly state: PullState }) {
  const active = state.phase !== 'idle';
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        opacity: active ? 1 : 0,
        transform: `translateY(${Math.round(state.distance)}px)`,
        transition: reduced ? 'opacity 120ms linear' : 'opacity 160ms ease-out',
        color: 'var(--text-tertiary)',
      }}
    >
      <Icon
        name="spinner"
        size={20}
        {...(state.phase === 'refreshing' && !reduced ? { className: 'button__spinner' } : {})}
      />
    </div>
  );
}

function usePullToRefresh(
  ref: React.RefObject<HTMLDivElement | null>,
  onRefresh: () => void,
): { readonly state: PullState; readonly status: string } {
  const [state, setState] = useState<PullState>(initialPullState);
  const [refreshedOnce, setRefreshedOnce] = useState(false);
  /* Read inside the listeners without re-subscribing them on every render. */
  const latest = useRef(state);
  latest.current = state;

  const dispatch = useCallback(
    (event: Parameters<typeof reducePull>[1]) => {
      const result = reducePull(latest.current, event);
      latest.current = result.state;
      setState(result.state);
      if (result.refresh) onRefresh();
    },
    [onRefresh],
  );

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const start = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      dispatch({ type: 'start', y: touch.clientY, scrollTop: node.scrollTop, now: Date.now() });
    };
    const move = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      dispatch({ type: 'move', y: touch.clientY, scrollTop: node.scrollTop });
    };
    const end = () => dispatch({ type: 'end', now: Date.now() });
    const cancel = () => dispatch({ type: 'cancel' });

    node.addEventListener('touchstart', start, { passive: true });
    node.addEventListener('touchmove', move, { passive: true });
    node.addEventListener('touchend', end, { passive: true });
    node.addEventListener('touchcancel', cancel, { passive: true });

    return () => {
      node.removeEventListener('touchstart', start);
      node.removeEventListener('touchmove', move);
      node.removeEventListener('touchend', end);
      node.removeEventListener('touchcancel', cancel);
    };
  }, [ref, dispatch]);

  useEffect(() => {
    if (state.phase !== 'refreshing') return;
    /*
     * The screen is remounted, so there is nothing to await. The delay is the
     * indicator's own minimum: a refresh that finishes in 40ms and takes the
     * spinner with it reads as nothing having happened.
     */
    const timer = setTimeout(() => {
      setRefreshedOnce(true);
      setState((current) => reducePull(current, { type: 'refreshed' }).state);
    }, 450);
    return () => clearTimeout(timer);
  }, [state.phase]);

  return { state, status: pullStatusText(state.phase, refreshedOnce) };
}

export function App() {
  const [route, navigate] = useHashRoute();
  /* Bumped by a pull; part of the screen's key, so a refresh is a remount. */
  const [refreshNonce, setRefreshNonce] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const onRefresh = useCallback(() => setRefreshNonce((value) => value + 1), []);
  const pull = usePullToRefresh(scrollRef, onRefresh);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => initTelegram(), []);

  // Authentication is the first thing that happens: every other call needs it.
  useEffect(() => {
    let cancelled = false;

    api
      .authenticate()
      .then((result) => {
        if (!cancelled) {
          setSession(result);
          setReady(true);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAuthError(
          error instanceof ApiError
            ? error.message
            : 'Не удалось войти. Откройте магазин через Telegram.',
        );
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const resolved = useMemo(() => resolveRoute(route), [route]);

  /*
   * Nested is "you arrived here from somewhere else in the app": a product, an
   * order, and the catalogue. Telegram's own back button covers those, which is
   * why none of these screens draws one.
   *
   * The catalogue is nested now whether or not it is filtered. It stopped being
   * a tab, so there is no longer any way to be on it except by having come from
   * Home — and a screen with no tab lit under it and no back button above it is
   * a dead end.
   */
  const isNested =
    resolved.kind === 'product' ||
    resolved.kind === 'order' ||
    resolved.kind === 'catalog' ||
    /* Поддержка lost its tab and is now reached from Профиль, so it needs the
       same way back as any other screen with no cell of its own. */
    resolved.kind === 'support' ||
    /* Every management screen was opened from Профиль, so every one of them
       needs the way back Telegram draws for us. */
    resolved.kind === 'admin';

  useEffect(() => useBackButton(isNested, () => window.history.back()), [isNested, route]);

  const screen = useMemo(() => {
    switch (resolved.kind) {
      case 'product':
        return <ProductScreen shortId={resolved.shortId} onNavigate={navigate} />;
      case 'order':
        return <OrderScreen orderId={resolved.orderId} />;
      case 'catalog':
        return <CatalogScreen categoryId={resolved.categoryId} onNavigate={navigate} />;
      case 'orders':
        return <OrdersScreen onNavigate={navigate} />;
      case 'support':
        return <SupportScreen />;
      case 'profile':
        return <ProfileScreen session={session} onNavigate={navigate} />;
      case 'admin':
        return <AdminSection route={resolved.admin} session={session} onNavigate={navigate} />;
      default:
        return <HomeScreen storeName={STORE_NAME} onNavigate={navigate} />;
    }
  }, [resolved, session]);

  if (!ready) {
    /*
     * The bar is chrome, not content: it belongs to the shell and is already
     * paid for in the scroller's bottom inset, so drawing it during the launch
     * placeholder is one less thing that appears out of nowhere. It stays live —
     * a tap during the half-second of authentication moves the light and picks
     * the screen that lands.
     */
    return (
      <div className="app">
        <div className="app__scroll">
          <LaunchSkeleton home={resolved.tab === '/'} />
        </div>
        <TabBar current={resolved.tab} onNavigate={navigate} />
      </div>
    );
  }

  if (authError) {
    const inside = isInsideTelegram();

    /*
     * One action, not two. Outside Telegram there is no signed `initData` to
     * retry with, so "Повторить" is a button that is guaranteed to fail the same
     * way; the only move that helps is the one that gets you inside. Inside
     * Telegram the failure is transient and retrying is the only move there is.
     * Whichever is offered is a plain capsule centred under the state, so it
     * reads as part of it rather than as a full-width bar orphaned below.
     */
    return (
      <div className="app">
        <div className="app__scroll">
          <div className="container">
            <ErrorState
              title="Вход не выполнен"
              text={
                inside ? authError : 'Магазин открывается из Telegram — запустите его через бота.'
              }
              onRetry={inside ? () => window.location.reload() : undefined}
            />
            {!inside && BOT_LINK ? (
              <div className="row" style={{ justifyContent: 'center' }}>
                <Button
                  variant="secondary"
                  onClick={() => window.open(BOT_LINK, '_blank', 'noopener')}
                >
                  Открыть бота
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="app__scroll" ref={scrollRef}>
        {/*
         * The gesture's only visible part, and it takes no space in the flow.
         *
         * Absolutely positioned above the first line of content and revealed by
         * the pull's own distance, so a screen that is not being pulled is laid
         * out exactly as it was — nothing here can move the sticky pay button or
         * change a section's height, which is what the rest of this app's layout
         * tests are about.
         */}
        <PullIndicator state={pull.state} />
        {/*
         * The refresh, as a remount.
         *
         * Every screen loads through `useAsync` on mount, so re-keying is a
         * refetch of everything the screen shows without the shell having to
         * know what any of it is. The cost is honest and worth stating: local
         * state goes with it — a half-typed promo code, a chosen variant. That
         * is what a native pull-to-refresh does too, and it is why the gesture
         * is deliberately hard to trigger by accident.
         *
         * Keyed by route as well: a boundary that has caught stays caught, so
         * without that the first screen to fail would keep failing after you
         * left it.
         */}
        <ScreenBoundary key={`${route}:${refreshNonce}`}>{screen}</ScreenBoundary>
      </div>
      {/*
       * What a screen reader is told. Off-screen, never focusable, and only ever
       * carrying the two states that are events — see `pullStatusText`.
       */}
      <p role="status" aria-live="polite" style={VISUALLY_HIDDEN}>
        {pull.status}
      </p>
      <TabBar current={resolved.tab} onNavigate={navigate} />
    </div>
  );
}
